/**
 * useResidentADLList Hook
 * CCH Care — Phase 3
 *
 * Changes:
 * - Accepts shiftType string instead of full shift object
 * - Fetches assigned residents by shift type (no care_shift_id needed)
 * - Fetches all facility residents separately for "Other Residents" section
 * - Exposes assignedResidents + otherResidents separately
 * - Search filters across both lists
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAssignedResidents, getAllResidents, searchResidents, listADLRecords } from '../api/adl';

export default function useResidentADLList(shiftType = null) {
  const [assignedResidents, setAssignedResidents] = useState([]);
  const [allResidents, setAllResidents]           = useState([]);
  const [selectedResident, setSelectedResident]   = useState(null);
  const [adlHistory, setAdlHistory]               = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [historyLoading, setHistoryLoading]       = useState(false);
  const [error, setError]                         = useState(null);

  // Search state
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);

  // Date filter for history view
  const [historyDate, setHistoryDate] = useState(null);

  // ── Fetch assigned residents for shift type ──────────────
  // Backend resolves care_shift_id from membership + shift_type + today
  const fetchAssigned = useCallback(async () => {
    if (!shiftType) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getAssignedResidents(null, shiftType);
      if (data.success) setAssignedResidents(data.residents || []);
    } catch (err) {
      setError(err.message || 'Failed to load assigned residents');
    } finally {
      setLoading(false);
    }
  }, [shiftType]);

  // ── Fetch all facility residents ─────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const data = await getAllResidents();
      if (data.success) setAllResidents(data.residents || []);
    } catch (err) {
      console.error('Failed to load all residents:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchAssigned();
    fetchAll();
  }, [fetchAssigned, fetchAll]);

  // ── Search (filters across both lists when query present) ─
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const data = await searchResidents(searchQuery.trim());
        if (data.success) setSearchResults(data.residents || []);
      } catch (err) {
        console.error('Resident search failed:', err.message);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Load ADL history when resident selected ──────────────
  const selectResident = useCallback(async (resident) => {
    setSelectedResident(resident);
    setAdlHistory([]);
    if (!resident?.patient_id) return;
    try {
      setHistoryLoading(true);
      const data = await listADLRecords({
        patientId: resident.patient_id,
        ...(historyDate && { shiftDate: historyDate }),
      });
      if (data.success) setAdlHistory(data.records || []);
    } catch (err) {
      console.error('Failed to load ADL history:', err.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyDate]);

  const clearSelection = useCallback(() => {
    setSelectedResident(null);
    setAdlHistory([]);
  }, []);

  // ── Derived lists ────────────────────────────────────────

  // IDs of assigned residents for quick lookup
  const assignedIds = useMemo(() =>
    new Set(assignedResidents.map(r => r.patient_id)),
  [assignedResidents]);

  // Other residents = all residents not in assigned list
  const otherResidents = useMemo(() =>
    allResidents.filter(r => !assignedIds.has(r.patient_id)),
  [allResidents, assignedIds]);

  // When searching: split search results into assigned vs other
  const searchAssigned = useMemo(() =>
    searchResults.filter(r => assignedIds.has(r.patient_id)),
  [searchResults, assignedIds]);

  const searchOther = useMemo(() =>
    searchResults.filter(r => !assignedIds.has(r.patient_id)),
  [searchResults, assignedIds]);

  const isSearching = searchQuery.trim().length >= 2;

  // ── Stats ────────────────────────────────────────────────
  const stats = useMemo(() => {
    const list    = assignedResidents;
    const total   = list.length;
    const complete   = list.filter(r => r.adl_complete).length;
    const inProgress = list.filter(r => r.adl_record_id && !r.adl_complete).length;
    const notStarted = list.filter(r => !r.adl_record_id).length;
    return { total, complete, inProgress, notStarted };
  }, [assignedResidents]);

  return {
    // Separated lists
    assignedResidents: isSearching ? searchAssigned : assignedResidents,
    otherResidents:    isSearching ? searchOther    : otherResidents,
    loading,
    error,
    stats,

    // Search
    searchQuery,
    setSearchQuery,
    searching,
    isSearching,

    // Selection
    selectedResident,
    selectResident,
    clearSelection,

    // ADL history
    adlHistory,
    historyLoading,
    historyDate,
    setHistoryDate,

    // Refresh
    refresh: fetchAssigned,
  };
}